/**
 * File type detection for the unified import system.
 * Detects chemistry file types from extension and content sniffing.
 */

export type DetectedFileType =
  | 'sdf'
  | 'mol'
  | 'smi'
  | 'cif'
  | 'csv'
  | 'xlsx'
  | 'rxn'
  | 'rdf'
  | 'fasta'
  | 'helm'
  | 'pdb'
  | 'pptx'
  | 'dwar'
  | 'pdf'
  | 'html'
  | 'zip'
  | 'unknown';

export interface FileDetectionResult {
  type: DetectedFileType;
  label: string;
  icon: string; // emoji for quick display
  category: 'molecules' | 'reactions' | 'sequences' | 'presentation' | 'tabular' | 'document' | 'unknown';
}

const EXTENSION_MAP: Record<string, FileDetectionResult> = {
  '.sdf': { type: 'sdf', label: 'SDF (Structure-Data)', icon: 'M', category: 'molecules' },
  '.mol': { type: 'mol', label: 'MOL (Molfile)', icon: 'M', category: 'molecules' },
  '.smi': { type: 'smi', label: 'SMILES', icon: 'S', category: 'molecules' },
  '.cif': { type: 'cif', label: 'CIF (Crystallographic)', icon: 'C', category: 'molecules' },
  '.csv': { type: 'csv', label: 'CSV', icon: 'T', category: 'tabular' },
  '.xlsx': { type: 'xlsx', label: 'Excel', icon: 'T', category: 'tabular' },
  '.xls': { type: 'xlsx', label: 'Excel', icon: 'T', category: 'tabular' },
  '.rxn': { type: 'rxn', label: 'RXN (Reaction)', icon: 'R', category: 'reactions' },
  '.rdf': { type: 'rdf', label: 'RDF (Reaction Data)', icon: 'R', category: 'reactions' },
  '.fasta': { type: 'fasta', label: 'FASTA', icon: 'B', category: 'sequences' },
  '.fa': { type: 'fasta', label: 'FASTA', icon: 'B', category: 'sequences' },
  '.faa': { type: 'fasta', label: 'FASTA (Protein)', icon: 'B', category: 'sequences' },
  '.fna': { type: 'fasta', label: 'FASTA (Nucleotide)', icon: 'B', category: 'sequences' },
  '.ffn': { type: 'fasta', label: 'FASTA', icon: 'B', category: 'sequences' },
  '.helm': { type: 'helm', label: 'HELM (Biopolymer)', icon: 'B', category: 'sequences' },
  '.pdb': { type: 'pdb', label: 'PDB (Protein Structure)', icon: 'B', category: 'sequences' },
  '.pptx': { type: 'pptx', label: 'PowerPoint', icon: 'P', category: 'presentation' },
  '.dwar': { type: 'dwar', label: 'DataWarrior', icon: 'D', category: 'tabular' },
  '.pdf': { type: 'pdf', label: 'PDF', icon: 'F', category: 'tabular' },
  '.html': { type: 'html', label: 'HTML', icon: 'H', category: 'document' },
  '.htm': { type: 'html', label: 'HTML', icon: 'H', category: 'document' },
  '.zip': { type: 'zip', label: 'ZIP (Bulk Scientific Files)', icon: 'Z', category: 'sequences' },
};

/**
 * Detect file type from filename extension.
 * For binary formats (xlsx, pptx), extension is the only reliable method.
 * For text formats, we can also sniff content.
 * Handles compressed files: .sdf.gz, .csv.gz, etc. → detects base type.
 */
export function detectFileType(file: File): FileDetectionResult {
  const name = file.name.toLowerCase();

  // Handle .gz: strip and detect base type (e.g. .sdf.gz → .sdf)
  if (name.endsWith('.gz')) {
    const baseName = name.slice(0, -3);
    const baseExt = '.' + baseName.split('.').pop();
    const byBaseExt = EXTENSION_MAP[baseExt];
    if (byBaseExt) return byBaseExt;
  }

  const ext = '.' + name.split('.').pop();

  const byExtension = EXTENSION_MAP[ext];
  if (byExtension) return byExtension;

  return { type: 'unknown', label: 'Unknown', icon: '?', category: 'unknown' };
}

/**
 * Async content-based detection for ambiguous files.
 * Reads first few KB and checks for signatures.
 */
export async function detectFileTypeFromContent(file: File): Promise<FileDetectionResult> {
  // Try extension first
  const extResult = detectFileType(file);
  if (extResult.type !== 'unknown') return extResult;

  // Read first 4KB for content sniffing
  const slice = file.slice(0, 4096);
  const text = await slice.text();

  // HELM also contains $$$$, so its signature must be checked before SDF.
  if (/(?:PEPTIDE|RNA|CHEM)\d+\{/i.test(text) && text.includes('$$$$')) {
    return EXTENSION_MAP['.helm'];
  }

  // SDF: contains $$$$
  if (text.includes('$$$$') || text.includes('V2000') || text.includes('V3000')) {
    return EXTENSION_MAP['.sdf'];
  }

  // MOL: starts with header + counts line with V2000/V3000
  if (text.match(/^\s*\n.*\n.*\n\s*\d+\s+\d+/)) {
    return EXTENSION_MAP['.mol'];
  }

  // CIF: starts with "data_" and contains "_atom_site"
  if (text.startsWith('data_') && text.includes('_atom_site')) {
    return EXTENSION_MAP['.cif'];
  }

  // FASTA: starts with >
  if (text.startsWith('>')) {
    return EXTENSION_MAP['.fasta'];
  }

  // SMILES: lines of SMILES-like patterns
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length > 0 && lines.every(l => /^[A-Za-z0-9@+\-[\]()\\/%=#$.]+(\s|$)/.test(l.trim()))) {
    return EXTENSION_MAP['.smi'];
  }

  // CSV: comma or tab separated with consistent column count
  if (lines.length > 1) {
    const commas = lines[0].split(',').length;
    const tabs = lines[0].split('\t').length;
    if (commas > 2 || tabs > 2) {
      return EXTENSION_MAP['.csv'];
    }
  }

  return extResult;
}

/** All accepted file extensions for the file input (includes .gz and .zip) */
export const ACCEPTED_EXTENSIONS = Object.keys(EXTENSION_MAP).join(',') + ',.gz,.zip';

/** Check if a file has an accepted extension */
export function isAcceptedFile(file: File): boolean {
  return detectFileType(file).type !== 'unknown';
}
